import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { CONTACT, IMAGES, EXPERIENCES, TESTIMONIALS, GALLERY } from '../mock/data';
import useScrollReveal from '../hooks/useScrollReveal';
import Seo from '../components/Seo';

const HERO_SLIDES = ['/hero-wedding.jpg', IMAGES.heroDining, IMAGES.banquet2, IMAGES.wedding1];

export default function Home() {
  useScrollReveal();
  const [slide, setSlide] = useState(0);

  const next = useCallback(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), []);
  const prev = () => setSlide((s) => (s - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);

  useEffect(() => {
    const id = setInterval(next, 5500);
    return () => clearInterval(id);
  }, [next]);

  return (
    <div>
      <Seo
        path="/"
        description="Vivamore Dining — a Western-Asian comfort food destination on an elegant rooftop at Level 6, NU Sentral Mall, Kuala Lumpur. Perfect for events, celebrations and private walk-in dining."
        keywords="Vivamore Dining, rooftop restaurant Kuala Lumpur, NU Sentral dining, Western Asian comfort food KL, event venue KL, private dining KL, celebration venue Kuala Lumpur"
      />
      {/* HERO SLIDER */}
      <section className="relative h-screen min-h-[640px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          {HERO_SLIDES.map((src, i) => (
            <div
              key={src}
              className={`absolute inset-0 transition-opacity duration-[1400ms] ease-in-out ${
                i === slide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={src}
                alt="Vivamore"
                className={`w-full h-full object-cover ${i === slide ? 'animate-zoom-slow' : ''}`}
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/40 to-ink/80" />
        </div>

        {/* Arrows */}
        <button
          onClick={prev}
          aria-label="Previous slide"
          className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center border border-cream/30 text-cream/80 hover:bg-cream hover:text-ink transition-colors"
        >
          <ChevronLeft size={22} />
        </button>
        <button
          onClick={next}
          aria-label="Next slide"
          className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center border border-cream/30 text-cream/80 hover:bg-cream hover:text-ink transition-colors"
        >
          <ChevronRight size={22} />
        </button>

        <div className="relative z-10 text-center px-6 max-w-4xl">
          <span className="text-cream/80 text-xs md:text-sm tracking-luxe uppercase animate-fade-in">Welcome to Vivamore</span>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-cream mt-6 leading-[1.05] animate-fade-up">
            Elegant Dining &<br />Thoughtful Celebrations
          </h1>
          <p className="text-cream/85 font-light text-lg md:text-xl mt-8 animate-fade-up" style={{ animationDelay: '0.15s' }}>
            Beautiful experiences, within reach
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12 animate-fade-up" style={{ animationDelay: '0.3s' }}>
            <a href={CONTACT.whatsappReserve} target="_blank" rel="noopener noreferrer" className="btn-gold text-xs tracking-widest uppercase px-9 py-4 bg-gold text-cream border border-gold hover:bg-transparent hover:text-cream">
              Reserve A Table
            </a>
            <Link to="/plan-event" className="btn-gold text-xs tracking-widest uppercase px-9 py-4 border border-cream text-cream hover:bg-cream hover:text-ink">
              Plan Your Event
            </Link>
          </div>
        </div>
        {/* Slide dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-[3px] transition-all duration-500 ${
                i === slide ? 'w-10 bg-gold-light' : 'w-5 bg-cream/40 hover:bg-cream/70'
              }`}
            />
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div className="reveal img-hover-zoom">
            <img src={IMAGES.diningInterior} alt="About Vivamore" className="w-full h-[520px] object-cover" />
          </div>
          <div className="reveal">
            <span className="text-xs tracking-luxe uppercase text-gold">Welcome to</span>
            <h2 className="font-display text-4xl md:text-5xl text-ink mt-4">About Vivamore</h2>
            <div className="gold-line mt-6 mb-8" />
            <p className="text-ink/70 font-light leading-relaxed mb-5">
              Located at NU Mall Kuala Lumpur, Vivamore is a contemporary premium dining and intimate event destination designed for meaningful experiences and modern celebrations.
            </p>
            <p className="text-ink/70 font-light leading-relaxed mb-5">
              Built around the philosophy of <em className="text-gold not-italic font-normal">Convenient · Connect · Celebrate</em>, Vivamore offers elegant spaces where people come together to dine, gather, and create memorable moments in a sophisticated and welcoming atmosphere.
            </p>
            <p className="text-ink/70 font-light leading-relaxed mb-8">
              Whether for private dining, intimate celebrations, corporate gatherings, or social occasions, Vivamore combines refined hospitality, curated culinary experiences, and contemporary ambiance.
            </p>
            <Link to="/about" className="btn-gold inline-flex items-center gap-2 text-xs tracking-widest uppercase px-8 py-4 border border-ink text-ink hover:bg-ink hover:text-cream">
              Learn More <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* EXPERIENCES */}
      <section className="py-24 md:py-32 bg-ink">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal text-center flex flex-col items-center mb-16">
            <span className="text-xs tracking-luxe uppercase text-gold-light">Discover</span>
            <h2 className="font-display text-4xl md:text-5xl text-cream mt-4">Our Experiences</h2>
            <div className="gold-line mt-6" />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {EXPERIENCES.map((e, i) => (
              <Link to={e.link} key={e.title} className="reveal group relative h-[460px] overflow-hidden block" style={{ transitionDelay: `${i * 0.12}s` }}>
                <img src={e.img} alt={e.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="font-display text-3xl text-cream">{e.title}</h3>
                  <p className="text-cream/75 font-light text-sm mt-2 mb-4">{e.desc}</p>
                  <span className="inline-flex items-center gap-2 text-gold-light text-xs tracking-widest uppercase">
                    Explore <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
            {GALLERY.slice(0, 5).map((g, i) => (
              <div key={i} className={`img-hover-zoom overflow-hidden ${i === 0 ? 'col-span-2 row-span-2 md:h-[420px]' : 'h-[205px]'}`}>
                <img src={g.img} alt={g.cat} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
          <div className="reveal text-center">
            <p className="font-serif text-2xl text-ink/80 italic mb-6">Elegant Weddings · Corporate Events · Private Celebrations</p>
            <Link to="/gallery" className="btn-gold inline-flex items-center gap-2 text-xs tracking-widest uppercase px-8 py-4 border border-gold text-gold hover:bg-gold hover:text-cream">
              View Gallery <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-ink">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal text-center flex flex-col items-center mb-16">
            <span className="text-xs tracking-luxe uppercase text-gold-light">Kind Words</span>
            <h2 className="font-display text-4xl md:text-5xl text-cream mt-4">What Our Guests Say</h2>
            <div className="gold-line mt-6" />
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="reveal border border-cream/15 p-10" style={{ transitionDelay: `${i * 0.1}s` }}>
                <Quote size={30} className="text-gold-light mb-6" />
                <p className="font-serif text-xl text-cream/90 italic leading-relaxed mb-8">“{t.quote}”</p>
                <p className="text-gold-light text-sm tracking-wider">{t.name}</p>
                <p className="text-cream/50 text-xs tracking-widest uppercase mt-1">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 overflow-hidden">
        <img src={IMAGES.banquet2} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/75" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl md:text-5xl text-cream">Ready to experience Vivamore?</h2>
          <div className="gold-line mx-auto my-8" />
          <p className="text-cream/80 font-light text-lg">
            Create unforgettable moments with us. Whether it's an intimate dinner or a grand celebration, we're here to make it extraordinary.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <a href={CONTACT.whatsappReserve} target="_blank" rel="noopener noreferrer" className="btn-gold text-xs tracking-widest uppercase px-9 py-4 bg-gold text-cream border border-gold hover:bg-transparent">Reserve A Table</a>
            <Link to="/plan-event" className="btn-gold text-xs tracking-widest uppercase px-9 py-4 border border-cream text-cream hover:bg-cream hover:text-ink">Plan Your Event</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
